/**
 * The single object a sync call site talks to.
 *
 * It owns the item/counter bookkeeping that every call site used to re-implement — badly:
 * rows built without the person's photo, failures counted after the loop instead of as
 * they happened. It does NOT pick a surface: SyncProgressProvider shows every run as the
 * toast-corner card and promotes it to the modal only when the operator zooms.
 *
 * `setData` is injected so the whole thing is testable without React;
 * `useSyncReporter` (hooks/useSyncReporter) is the thin wiring for components.
 */

import { SyncProgressItem, SyncProgressState } from '../components/SyncProgressModal';
import { SyncSubject, buildPersonSyncItem, buildPersonSyncTitle } from './syncProgressPresenter';

export interface SyncReporterDeps {
  setData: (data: SyncProgressState | null) => void;
}

export interface SyncReporterOptions {
  /** The person being written to the devices — owner of every row. */
  subject: SyncSubject;
  total: number;
  /** Header line; defaults to "<person> — N dispositivo(s)". */
  title?: string;
  /** False when the caller cannot actually pause/stop (e.g. a Promise.all fan-out). */
  controls?: boolean;
}

export interface SyncReporter {
  start: () => void;
  record: (deviceName: string, status: 'ok' | 'failed', error?: string) => void;
  finish: () => void;
  abort: () => void;
}

export function createSyncReporter(
  { subject, total, title, controls }: SyncReporterOptions,
  deps: SyncReporterDeps,
): SyncReporter {
  const zoneName = title ?? buildPersonSyncTitle(subject.name, total);
  const items: SyncProgressItem[] = [];
  let ok = 0;
  let fail = 0;

  const push = (isComplete: boolean) => {
    deps.setData({
      zoneName,
      personPhotoUrl: subject.photoUrl,
      total,
      done: items.length,
      ok,
      fail,
      recentItems: [...items],
      isComplete,
      ...(controls === false ? { controls: false } : {}),
    });
  };

  return {
    start() {
      push(false);
    },

    record(deviceName, status, error) {
      if (status === 'ok') ok++;
      else fail++;
      items.push(buildPersonSyncItem({ person: subject, deviceName, status, error }));
      push(false);
    },

    finish() {
      push(true);
    },

    abort() {
      deps.setData(null);
    },
  };
}
